// Phase 24A — Shared XLSX + PDF export buttons.
// Uses fetch with Authorization header (axios doesn't handle blob elegantly
// through the response interceptor for our 401 behaviour, so we go direct).
import { useState } from "react";
import { toast } from "sonner";
import { FileDown, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API_BASE, getToken, formatApiError } from "@/lib/api";

export default function ExportButtons({ endpoint, params = {}, filename }) {
  const [busy, setBusy] = useState(null); // "xlsx" | "pdf" | null

  async function download(format) {
    setBusy(format);
    try {
      const q = new URLSearchParams({ ...params, format }).toString();
      const url = `${API_BASE}${endpoint}?${q}`;
      const res = await fetch(url, {
        headers: {
          "Authorization": `Bearer ${getToken()}`,
          "X-App-Build": process.env.REACT_APP_BUILD_ID || "dev-local",
        },
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || `Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${filename}.${format}`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(null); }
  }

  return (
    <div className="flex gap-2" data-testid="export-buttons">
      <Button size="sm" variant="outline" disabled={busy != null}
                onClick={() => download("xlsx")}
                data-testid="export-xlsx-btn">
        {busy === "xlsx" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}
        XLSX
      </Button>
      <Button size="sm" variant="outline" disabled={busy != null}
                onClick={() => download("pdf")}
                data-testid="export-pdf-btn">
        {busy === "pdf" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
        PDF
      </Button>
    </div>
  );
}
